import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getUserId, logEvent } from '../analytics';
import { celebrationEventName, isValidMilestone, selectMilestone } from '../utils/milestones';
import { setMilestoneOwner, subscribeMilestoneResponses } from '../utils/milestoneResponses';
import { createMilestoneCard } from '../utils/milestoneCard';
import { getSessionStatuses } from '../api/gameplay';
import { getAllGames } from '../games';
import { getCurrentDate } from '../util';
import { config } from '../config';

const MilestoneContext = createContext({ pending: [], finishMilestone: () => {}, checkDailyCircuit: () => {}, hasPendingMilestones: false });
export const useMilestones = () => useContext(MilestoneContext);
export const milestoneAnalytics = (event) => event.kind === 'daily_circuit'
  ? { circuit_date: event.date, game_count: event.total }
  : { resource: event.resource, threshold: event.threshold, value: event.value };
export const MilestoneProvider = ({ children }) => {
  const { isLoaded, userId, isSignedIn, getToken } = useAuth();
  const owner = isLoaded ? (isSignedIn ? `user:${userId}` : `guest:${getUserId() || 'anonymous'}`) : 'loading';
  const ownerRef = useRef(owner);
  const seenRef = useRef(new Set());
  const checksRef = useRef(new Set());
  const generationRef = useRef(0);
  const [state, setState] = useState({ owner, pending: [], checking: [] });
  const enqueueEvent = useCallback((event) => {
    const generation = generationRef.current;
    const pending = { event, file: null, readyAt: Date.now() + 500 };
    setState((current) => ({ ...current, pending: [...current.pending, pending] }));
    createMilestoneCard(event).then((file) => {
      if (generationRef.current !== generation) return;
      setState((current) => ({ ...current, pending: current.pending.map((item) => item.event.id === event.id ? { ...item, file } : item) }));
    }).catch(() => {
      if (generationRef.current === generation) logEvent(celebrationEventName(event, 'share_error'), { ...milestoneAnalytics(event), stage: 'image_generation' });
    });
  }, []);
  useLayoutEffect(() => {
    setMilestoneOwner(owner);
    if (ownerRef.current !== owner) {
      ownerRef.current = owner;
      seenRef.current = new Set();
      checksRef.current = new Set();
      setState({ owner, pending: [], checking: [] });
    }
    const unsubscribe = subscribeMilestoneResponses((events) => {
      if (!isLoaded) return;
      const unseen = events.filter((event) => isValidMilestone(event) && !seenRef.current.has(event.id));
      unseen.forEach((event) => seenRef.current.add(event.id));
      const eligible = unseen.filter((event) => isSignedIn || event.resource === 'streak');
      eligible.forEach((event) => logEvent('milestone_eligible', milestoneAnalytics(event)));
      const event = selectMilestone(eligible);
      if (!event) return;
      enqueueEvent(event);
    });
    return () => { unsubscribe(); generationRef.current += 1; };
  }, [owner, isLoaded, isSignedIn, enqueueEvent]);
  const checkDailyCircuit = useCallback(async ({ gameSlug, date, getPuzzleInstanceId }) => {
    if (!isLoaded || ownerRef.current !== owner || date !== getCurrentDate()) return;
    const id = `daily-circuit:${owner}:${date}`;
    const games = getAllGames().filter((game) => !game.hidden);
    if (!games.some((game) => game.id === gameSlug) || seenRef.current.has(id) || checksRef.current.has(id)) return;
    const puzzleIds = games.map((game) => getPuzzleInstanceId(game.id, date));
    if (puzzleIds.some((puzzleId) => !puzzleId) || new Set(puzzleIds).size !== games.length) return;
    const generation = generationRef.current;
    const isCurrent = () => generationRef.current === generation && ownerRef.current === owner;
    checksRef.current.add(id);
    setState((current) => ({ ...current, checking: [...current.checking, id] }));
    try {
      // A fresh account-wide read includes games finished on another device;
      // the local status cache may still show those games as unstarted.
      const token = isSignedIn ? await getToken({ template: 'basic_user_info_jwt_template' }) : null;
      if (!isCurrent() || (isSignedIn && !token)) return;
      const response = await getSessionStatuses(puzzleIds, token);
      if (!isCurrent() || date !== getCurrentDate() || !response?.success || !Array.isArray(response.statuses)) return;
      const statuses = new Map(response.statuses.map((item) => [item.puzzle_instance_id, item.status]));
      if (!puzzleIds.every((puzzleId) => ['won', 'lost', 'completed'].includes(statuses.get(puzzleId)))) return;
      seenRef.current.add(id);
      const event = { id, kind: 'daily_circuit', date, total: games.length };
      logEvent('daily_circuit_eligible', milestoneAnalytics(event));
      enqueueEvent(event);
    } catch (_) {
      // Optional celebration checks must never interrupt completion/results.
    } finally {
      if (isCurrent()) {
        checksRef.current.delete(id);
        setState((current) => ({ ...current, checking: current.checking.filter((item) => item !== id) }));
      }
    }
  }, [owner, isLoaded, isSignedIn, getToken, enqueueEvent]);
  const previewDailyCircuit = useCallback(() => {
    if (config.ENVIRONMENT !== 'development') return;
    enqueueEvent({ id: `dev-daily-circuit:${Date.now()}`, kind: 'daily_circuit', date: getCurrentDate(),
      total: getAllGames().filter((game) => !game.hidden).length });
  }, [enqueueEvent]);
  const finishMilestone = useCallback((id) => setState((current) => ({ ...current,
    pending: current.pending.filter((item) => item.event.id !== id),
  })), []);
  const value = useMemo(() => {
    const pending = state.owner === owner ? state.pending : [];
    return { pending, hasPendingMilestones: pending.length > 0 || (state.owner === owner && state.checking.length > 0),
      finishMilestone, checkDailyCircuit, previewDailyCircuit,
    };
  }, [state, owner, finishMilestone, checkDailyCircuit, previewDailyCircuit]);
  return <MilestoneContext.Provider value={value}>{children}</MilestoneContext.Provider>;
};
